import React from 'react';
import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ResponsiveWrapper from 'components/charts/ResponsiveWrapper';

const useStyles = makeStyles((theme) => ({
    container: {
        margin: theme.spacing(1.5),
        borderRadius: `${theme.spacing(1)}px ${theme.spacing(1)}px 0px 0px`,
        color: theme.palette.text.primary,
        overflowX: "auto",
        maxHeight: ({ height }) => height - 30,
        height: ({ height }) => height,
        backgroundColor: theme.palette.table?.primaryBase,
        '&::-webkit-scrollbar': {
            height: 6,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.action.disabled,
        },
    },
    table: {
        borderCollapse: "collapse",
        width: "max-content",
        minWidth: "100%",
        tableLayout: "auto"
    },
    thead: {
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backgroundColor: theme.palette.secondary?.base,
    },
    emptyState: {
        height: ({ height }) => height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    thFixed: { padding: theme.spacing(1, 1.25), textAlign: "left", ...theme.typography.body1, whiteSpace: 'nowrap' },
    thGroup: { padding: theme.spacing(1, 1), textAlign: "center", whiteSpace: 'nowrap' },
    thSub: { padding: theme.spacing(0.9, 0.8), textAlign: "center", opacity: 0.85, minWidth: 62, whiteSpace: 'nowrap' },
    tdFixed: { padding: theme.spacing(1, 1.25), textAlign: "left", ...theme.typography.body1, whiteSpace: 'nowrap' },
    tdCategory: { padding: theme.spacing(1, 1.25), textAlign: "left", textTransform: 'capitalize', ...theme.typography.body1, whiteSpace: 'nowrap' },
    td: { padding: theme.spacing(0.75, 0.8), textAlign: "center", minWidth: 62, whiteSpace: 'nowrap' },
    stickyHeaderCell: {
        position: 'sticky',
        top: 0,
        zIndex: 4,
        backgroundColor: theme.palette.secondary?.base,
    },
    stickyBodyCell: {
        position: 'sticky',
        zIndex: 1,
        backgroundColor: theme.palette.table?.primaryBase,
    },
    row: { borderBottom: `1px solid ${theme.palette.divider}` }
}));

const getNestedValue = (obj, path, def = '-') =>
    path.split('.').reduce((acc, key) => acc?.[key], obj) ?? def;

const getYears = (rows, columnsKey) =>
    [...new Set(
        rows.flatMap(row =>
            Object.keys(getNestedValue(row, columnsKey, {}))
        )
    )].sort((a, b) => Number(a) - Number(b));

const estimateTextWidth = (value) => {
    const text = value == null ? '' : String(value);
    return text.length * 8 + 28;
};

const calculateColumnWidths = ({
        fixedColumns,
        tableRows,
        getWidth,
        t,
    }) =>
        fixedColumns.map((col, idx) =>
            Math.max(
                getWidth(idx),
                estimateTextWidth(t(col.label)),
                ...tableRows.map(row =>
                    estimateTextWidth(row.fixedCells[idx])
            )
        )
    );

const GroupedDataTable = ({
    data = [],
    loading = false,
    fixedColumns = [],
    height = 300,
    columnsKey = 'years',
    subcolumns = ['Q1', 'Q2', 'Q3', 'Q4'],
    categories = [],
    groupByKeys = ['originCountry', 'destinationCountry', 'customer.name'],
    fixedColumnWidths = [130, 130, 130, 130],
    stickyColumns = 4,
    stickyEnabled = true,
    testId = 'grouped-data-table',
}) => {
    const classes = useStyles({ height });
    const { t } = useTranslation();

    if (loading || !Array.isArray(data) || data.length === 0) {
        const text = loading ? "loading" : "no_data_found";
        return (
            <div data-testid={testId} className={classes.emptyState}>
                {t(text)}
            </div>
        );
    }

    const years = getYears(data, columnsKey);

    const getFixedColumnWidth = (colIndex) =>
        fixedColumns[colIndex].width || fixedColumnWidths[colIndex] || 120;

    const buildDefaultFixedCells = (row, category, categoryIndex) => {
        return fixedColumns.map((_col, colIndex) => {
            if (colIndex < groupByKeys.length) {
                return categoryIndex === 0 ? getNestedValue(row, groupByKeys[colIndex]) : "";
            }

            if (colIndex === groupByKeys.length && category.label) {
                return t(category.label);
            }

            return "";
        });
    };

    const tableRows = data.flatMap(row =>
        categories.map((category, index) => ({
            fixedCells: buildDefaultFixedCells(row, category, index),

            categoryKey: category.valueKey,
            values: getNestedValue(row, columnsKey, {}),
        }))
    );
    
    const getCellValue = ({ values, year, subcolumn, categoryKey }) =>
        values?.[year]?.[subcolumn]?.[categoryKey] ?? '';

    const columnWidths = calculateColumnWidths({
        fixedColumns,
        tableRows,
        getWidth: getFixedColumnWidth,
        t,
    });

    const stickyOffsets = columnWidths.map((_width, index) =>
        columnWidths.slice(0, index).reduce((sum, width) => sum + width, 0)
    );

    const getStickyStyles = (colIndex, baseZIndex) => {
        if (!stickyEnabled || colIndex >= stickyColumns) return {};

        return {
            left: stickyOffsets[colIndex],
            zIndex: baseZIndex + stickyColumns - colIndex,
        };
    };

    return (
        <div className={classes.container} data-testid={testId}>
            <table className={classes.table}>
                <thead className={classes.thead}>
                    <tr>
                        {fixedColumns.map((col, colIndex) => (
                            <th
                                key={col.key}
                                rowSpan={2}
                                className={`${classes.thFixed} ${stickyEnabled && colIndex < stickyColumns ? classes.stickyHeaderCell : ''}`}
                                style={{
                                    boxSizing: 'border-box',
                                    width: columnWidths[colIndex],
                                    minWidth: columnWidths[colIndex],
                                    ...getStickyStyles(colIndex, 30),
                                }}
                            >
                                {t(col.label)}
                            </th>
                        ))}
                        {years.map((year) => (
                            <th key={year} colSpan={subcolumns.length} className={classes.thGroup}>
                                {year}
                            </th>
                        ))}
                    </tr>
                    <tr>
                        {years.flatMap((year) =>
                            subcolumns.map((subcolumn) => (
                                <th key={`${year}-${subcolumn}`} className={classes.thSub}>
                                    {t(subcolumn)}
                                </th>
                            ))
                        )}
                    </tr>
                </thead>
                <tbody>
                    {tableRows.map((row, i) => (
                        <tr key={i} className={tableRows.length > 4 && (i + 1) % 4 === 0 ? classes.row : ''}>
                            {row.fixedCells.map((value, colIndex) => (
                                <td
                                    key={`${i}-fixed-${colIndex}`}
                                    className={`${colIndex >= groupByKeys.length ? classes.tdCategory : classes.tdFixed} ${stickyEnabled && colIndex < stickyColumns ? classes.stickyBodyCell : ''}`}
                                    style={{
                                        boxSizing: 'border-box',
                                        width: columnWidths[colIndex],
                                        minWidth: columnWidths[colIndex],
                                        ...getStickyStyles(colIndex, 30)
                                    }}
                                >
                                    {value}
                                </td>
                            ))}

                            {years.flatMap(year =>
                                subcolumns.map(col => (
                                    <td key={`${i}-${year}-${col}`} className={classes.td}>
                                        {getCellValue({
                                            values: row.values,
                                            year,
                                            subcolumn: col,
                                            categoryKey: row.categoryKey,
                                        })}
                                    </td>
                                ))
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

GroupedDataTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    fixedColumns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        width: PropTypes.number,
    })),
    height: PropTypes.number,
    columnsKey: PropTypes.string,
    subcolumns: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        valueKey: PropTypes.string,
    })),
    groupByKeys: PropTypes.arrayOf(PropTypes.string),
    fixedColumnWidths: PropTypes.arrayOf(PropTypes.number),
    stickyColumns: PropTypes.number,
    stickyEnabled: PropTypes.bool,
    testId: PropTypes.string,
};

export default ResponsiveWrapper(GroupedDataTable);